import { Button, Card, Grid, RangeSlider, SegmentedControl, Switch, Text, TextInput } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import ISO6391 from 'iso-639-1';
import { useMemo } from 'react';
import ProductFeedSelector from '../ProductFeeds/ProductFeedSelector';
import SearchableCombobox from '../SearchableCombobox';
import TagsSelect from '../TagSelector';

const GeneralRecommenderForm = ({
  accountId,
  domainId,
  recommender,
  handleChange,
  setRecommender,
  setUserError,
  handleTranslationChange,
  translations,
  addTranslation,
  removeTranslation,
}) => {
  // Generate locale options dynamically from ISO6391
  const localeOptions = useMemo(
    () =>
      ISO6391.getAllCodes().map((code) => ({
        value: code,
        label: ISO6391.getName(code),
      })),
    []
  );
  return (
    <Card withBorder>
      <Card.Section inheritPadding py="md">
        <Grid align="center" mb="sm" data-tour="recommenderName">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text fw={500}>Name*</Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput name="name" value={recommender.name} onChange={handleChange} />
          </Grid.Col>
        </Grid>
        {/* Title and Translations */}
        <Grid align="center" mb="sm" data-tour="recommenderTitle">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text fw={500}>Title</Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput
              name="title"
              value={recommender.title}
              placeholder="e.g. Similar Products"
              onChange={handleChange}
            />
            <Text size="sm" c="dimmed" mt="xs">
              This is the title for the product block that your visitors will see above the list of products.
            </Text>
          </Grid.Col>
        </Grid>

        <Grid align="center" mb="sm">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text fw={500}>Title Translations</Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            {translations.map((item, index) => (
              <div key={index} style={{ marginBottom: '8px' }}>
                <Grid>
                  <Grid.Col span={{ base: 12, sm: 3 }}>
                    <SearchableCombobox
                      options={localeOptions}
                      onChange={(value) => handleTranslationChange(index, 'locale', { value })}
                      value={item.locale.value}
                      placeholder="Select Locale"
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 8 }}>
                    <TextInput
                      name="title"
                      value={item.translation}
                      placeholder="Translation"
                      onChange={(e) => handleTranslationChange(index, 'translation', e.target.value)}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 1 }}>
                    <Button onClick={() => removeTranslation(index)} color="red">
                      <IconX size={14} />
                    </Button>
                  </Grid.Col>
                </Grid>
              </div>
            ))}
            <Button onClick={addTranslation}>Add Translation</Button>
          </Grid.Col>
        </Grid>
        <Grid align="center" mb="sm">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text fw={500}>Tags</Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TagsSelect model={recommender} handleChange={handleChange} setUserError={setUserError} />
          </Grid.Col>
        </Grid>
        <hr />
        <Grid align="center" mb="sm" data-tour="recommenderGeneralMode">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text fw={500}>Mode*</Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <SegmentedControl
              value={recommender.mode ?? ''}
              onChange={(mode) => handleChange({ target: { name: 'mode', value: mode } })}
              data={[
                { label: 'Highlight', value: 'highlight' },
                { label: 'Discover', value: 'discover' },
                { label: 'Re-Rank', value: 'reRank' },
              ]}
            />
            <Text size="sm" c="dimmed" mt="xs">
              In Highlight Mode, the results might contain products already on the page. <br />
              In Discover Mode, product blocks will only return products not found on the page.
              <br />
              In Re-Rank Mode, the results will contain only products found on the page, re-ranked according to the
              algorithm and filter selection.
            </Text>
          </Grid.Col>
        </Grid>
        <Grid align="center" mb="sm" data-tour="recommenderProductFeed">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text fw={500}>Product Feed*</Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <ProductFeedSelector
              accountId={accountId}
              domainId={domainId}
              productFeedId={recommender.productFeedId}
              onSelect={(productFeedId) => setRecommender({ ...recommender, productFeedId })}
              forType="recommender"
            />
          </Grid.Col>
        </Grid>
        {recommender.mode !== 'reRank' ? (
          <Grid align="center" mb="sm" data-tour="recommenderProductsSlider">
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Text fw={500}>Products to Show*</Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <RangeSlider
                min={0}
                max={50}
                marks={[10, 20, 30, 40, 50].map((value) => ({ value, label: value }))}
                value={[recommender.minCount, recommender.count]}
                onChange={([minCount, count]) => setRecommender({ ...recommender, minCount, count })}
              />
              <Text size="sm" c="dimmed" mt="xl">
                This product block will show at most <strong>{recommender.count}</strong> products.
                <br />
                It will not show any products if the number of results is lower than{' '}
                <strong>{recommender.minCount}</strong>.
              </Text>
            </Grid.Col>
          </Grid>
        ) : (
          ''
        )}
        <Grid align="center" mb="sm">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text fw={500}>Randomize</Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Switch
              checked={recommender.randomize}
              onChange={(e) => handleChange({ target: { name: 'randomize', value: e.currentTarget.checked } })}
            />
            <Text size="sm" c="dimmed" mt="xs">
              Rearrange product order on every page refresh.
            </Text>
          </Grid.Col>
        </Grid>
      </Card.Section>
    </Card>
  );
};

export default GeneralRecommenderForm;
